import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div
        className="hidden lg:flex relative items-end p-12 text-white"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(45,50,47,0.55), rgba(92,113,94,0.55)), url('https://images.unsplash.com/photo-1587304798516-e17f7191a3a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxjYWxtJTIwbmF0dXJlJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODE4MTQyODR8MA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-md">
          <div className="overline text-white/80">For special education teachers</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium mt-3 leading-tight">
            Write better IEPs.<br/>Track what matters.
          </h1>
          <p className="mt-5 text-white/85 leading-relaxed">
            AI-guided plans, meetings, lessons, and progress — held together with care for every student you serve.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 paper">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-10 h-10 rounded-xl bg-[#6E5AA0] flex items-center justify-center">
              <Sparkles className="text-white" size={18} />
            </div>
            <span className="font-heading text-xl font-semibold ink">Ellis Path</span>
          </div>

          <h2 className="font-heading text-3xl font-medium ink">Welcome back</h2>
          <p className="ink-muted mt-2 text-sm">Sign in to your teacher workspace.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@school.edu"
                data-testid="login-email-input"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
                className="h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-[#6E5AA0] hover:bg-[#574785] text-white"
              data-testid="login-submit-button"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 text-sm ink-muted">
            New here?{" "}
            <Link to="/register" className="text-[#6E5AA0] font-medium hover:underline" data-testid="link-register">
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
